import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Phone, Mail, Facebook, Instagram, Twitter } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden" data-testid="footer">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl animate-floatSlow"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-[#E85A24] rounded-full filter blur-3xl animate-float"></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2 animate-fadeInUp">
            <div className="flex items-center gap-2 mb-6 group cursor-pointer">
              <span className="text-3xl font-extrabold text-blue-400 font-['Manrope'] transform group-hover:scale-105 transition-transform duration-300" style={{ fontStyle: 'italic' }}>
                UnitedHands
              </span>
              <span className="text-3xl font-extrabold bg-gradient-to-r from-[#E85A24] to-orange-500 bg-clip-text text-transparent font-['Manrope'] transform group-hover:scale-105 transition-transform duration-300">
                KARUR
              </span>
            </div>
            <p className="text-slate-300 mb-6 max-w-md text-lg leading-relaxed">
              {t('footerTagline')}
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-blue-600 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-blue-500/50">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-pink-600 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-pink-500/50">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-blue-400 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-blue-400/50">
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="animate-fadeInUp" style={{animationDelay: '0.1s'}}>
            <h3 className="font-bold text-xl mb-6 text-white">{t('quickLinks')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-slate-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group">
                  <span className="relative">
                    {t('home')}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E85A24] group-hover:w-full transition-all duration-300"></span>
                  </span>
                </Link>
              </li>
              <li>
                <Link to="/categories" className="text-slate-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group">
                  <span className="relative">
                    {t('categories')}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E85A24] group-hover:w-full transition-all duration-300"></span>
                  </span>
                </Link>
              </li>
              {/* Add Shop link hidden - manual shop approval only */}
              {/* <li>
                <Link to="/add-shop" className="text-slate-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group">
                  <span className="relative">
                    {t('addYourShop')}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E85A24] group-hover:w-full transition-all duration-300"></span>
                  </span>
                </Link>
              </li> */}
              <li>
                <Link to="/about" className="text-slate-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group">
                  <span className="relative">
                    {t('about')}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E85A24] group-hover:w-full transition-all duration-300"></span>
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="animate-fadeInUp" style={{animationDelay: '0.2s'}}>
            <h3 className="font-bold text-xl mb-6 text-white">{t('contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-slate-300 group hover:text-white transition-colors duration-300">
                <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0 text-[#E85A24] group-hover:scale-110 transition-transform duration-300" />
                <span>No. 90, Sri Velavan Complex<br />Vaiyapuri Nagar(West), Karur<br />TamilNadu, India - 639002</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300 group hover:text-white transition-colors duration-300">
                <Phone className="h-5 w-5 flex-shrink-0 text-[#E85A24] group-hover:scale-110 transition-transform duration-300" />
                <a href="tel:+917624919806" className="hover:text-white">+91-7624919806</a>
              </li>
              <li className="flex items-center gap-3 text-slate-300 group hover:text-white transition-colors duration-300">
                <Mail className="h-5 w-5 flex-shrink-0 text-[#E85A24] group-hover:scale-110 transition-transform duration-300" />
                <a href="mailto:admin@unitedhands.cc" className="hover:text-white">admin@unitedhands.cc</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              {t('copyright')}
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <a href="#" className="hover:text-white transition-colors duration-300">Privacy Policy</a>
              <span className="text-slate-600">•</span>
              <a href="#" className="hover:text-white transition-colors duration-300">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative gradient line at top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-[#E85A24]"></div>
    </footer>
  );
};

export default Footer;
